import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, AlertTriangle } from "lucide-react";

export default function Invitation() {
  const { token } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/invitation/${token}`).then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Lien invalide"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 10) return toast.error("10 caractères minimum");
    if (pwd !== pwd2) return toast.error("Les deux mots de passe diffèrent");
    setBusy(true);
    try {
      await api.post("/auth/invitation/accepter", { token, password: pwd });
      toast.success("Mot de passe enregistré : vous pouvez vous connecter");
      nav("/login", { replace: true });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDF9F1] px-4" data-testid="invitation-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8 space-y-6">
        <Logo />
        {error ? (
          <div className="space-y-4" data-testid="invitation-error">
            <div className="flex gap-2 text-red-700 text-sm"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{error}</div>
            <Link to="/login" className="text-sm underline text-slate-700" data-testid="invitation-login-link">Aller à la connexion</Link>
          </div>
        ) : !info ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <form onSubmit={submit} className="space-y-4" data-testid="invitation-form">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                {info.type === "reinitialisation" ? "Nouveau mot de passe" : "Activez votre compte"}
              </h1>
              <p className="text-sm text-slate-600 mt-1">Bonjour {info.name || ""}, choisissez votre mot de passe pour <strong data-testid="invitation-email">{info.email}</strong>.</p>
            </div>
            <div className="space-y-1.5"><Label htmlFor="pwd">Mot de passe (10 caractères minimum)</Label>
              <Input id="pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} autoComplete="new-password" data-testid="invitation-password" /></div>
            <div className="space-y-1.5"><Label htmlFor="pwd2">Confirmer</Label>
              <Input id="pwd2" type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} autoComplete="new-password" data-testid="invitation-password-confirm" /></div>
            <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="invitation-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}Enregistrer mon mot de passe
            </Button>
            <p className="text-xs text-slate-500">Lien valable jusqu'au {new Date(info.expires_at).toLocaleString("fr-FR")}, utilisable une seule fois.</p>
          </form>
        )}
      </div>
    </div>
  );
}
